/**
 * Team copy: accounts, invitations, admissions, the access history and the
 * administration dialogs.
 */

export interface TeamCopy {
  readonly team: {
    readonly title: string;
    readonly pendingAdmissions: string;
    readonly admissionsEmpty: string;
    readonly approve: string;
    readonly reject: string;
    readonly contributors: string;
    readonly invitations: string;
    readonly invitationsEmpty: string;
    readonly invite: string;
    readonly inviteEmail: string;
    readonly inviteRole: string;
    readonly inviteSubmit: string;
    readonly inviteCreatedTitle: string;
    readonly inviteCreatedBody: string;
    readonly copyLink: string;
    readonly linkCopied: string;
    readonly withdraw: string;
    readonly changeRole: string;
    readonly suspend: string;
    readonly restore: string;
    readonly deauthorize: string;
    readonly deauthorizeTitle: string;
    readonly deauthorizeBody: (name: string) => string;
    readonly deauthorizeEffects: readonly string[];
    readonly accessHistory: string;
    readonly accessHistoryEmpty: string;
    readonly accessHistoryColumns: {
      readonly when: string;
      readonly actor: string;
      readonly action: string;
      readonly subject: string;
    };
    readonly statuses: Readonly<Record<string, string>>;
    readonly lastOwnerTitle: string;
    readonly lastOwnerBody: string;
    readonly suspendTitle: string;
    readonly suspendBody: (name: string) => string;
    readonly suspendEffects: readonly string[];
    readonly roleTitle: (name: string) => string;
    readonly roleBody: string;
    readonly roleEffects: readonly string[];
    readonly provider: string;
    readonly providerEmail: string;
    readonly invitedAs: string;
    readonly emailUnverified: string;
  };
}

export const TEAM_COPY_EN: TeamCopy = {
  team: {
    title: 'Team and access',
    pendingAdmissions: 'Pending admissions',
    admissionsEmpty: 'Nobody is waiting to be admitted.',
    approve: 'Approve',
    reject: 'Reject',
    contributors: 'Contributors',
    invitations: 'Invitations',
    invitationsEmpty: 'No open invitations.',
    invite: 'Invite a contributor',
    inviteEmail: 'Email address',
    inviteRole: 'Role',
    inviteSubmit: 'Create the invitation',
    inviteCreatedTitle: 'Invitation created',
    inviteCreatedBody:
      'Share this single-use link with the person you invited. It expires in seven days.',
    copyLink: 'Copy the link',
    linkCopied: 'Link copied.',
    withdraw: 'Withdraw',
    changeRole: 'Change role',
    suspend: 'Suspend',
    restore: 'Restore',
    deauthorize: 'Deauthorize',
    deauthorizeTitle: 'Deauthorize this account',
    deauthorizeBody: (name) =>
      `${name} loses access for good. Their task, contribution and access history is preserved and keeps naming them.`,
    deauthorizeEffects: [
      'Their sessions end immediately.',
      'They cannot sign in again, and there is no restore.',
      'Coming back requires a fresh invitation, redeemed at sign-in.',
      'Their history is preserved, unchanged.',
    ],
    accessHistory: 'Access history',
    accessHistoryEmpty: 'No access events recorded yet.',
    accessHistoryColumns: {
      when: 'When',
      actor: 'Who acted',
      action: 'Action',
      subject: 'Acted on',
    },
    statuses: { active: 'Active', disabled: 'Suspended', deauthorized: 'Deauthorized' },
    lastOwnerTitle: 'The last active owner is protected',
    lastOwnerBody:
      'The system refuses to demote, suspend or remove the only active owner — otherwise nobody could administer access at all. Add another owner first.',
    suspendTitle: 'Suspend this account',
    suspendBody: (name) =>
      `${name} will lose access at their next request. Their contribution history is unchanged.`,
    suspendEffects: [
      'Their sessions end immediately.',
      'They cannot sign in until restored.',
      'Their contribution history is preserved.',
    ],
    roleTitle: (name) => `Change ${name}’s role`,
    roleBody: 'Pick the role this person should hold from now on.',
    roleEffects: [
      'Their sessions end and they sign in again with the new role.',
      'What they can see and do changes immediately.',
    ],
    provider: 'Provider',
    providerEmail: 'Provider email',
    invitedAs: 'Invited as',
    emailUnverified: 'unverified',
  },
};

export const TEAM_COPY_AR: TeamCopy = {
  team: {
    title: 'الفريق والوصول',
    pendingAdmissions: 'طلبات قبول معلّقة',
    admissionsEmpty: 'لا أحد بانتظار القبول.',
    approve: 'قبول',
    reject: 'رفض',
    contributors: 'المساهمون',
    invitations: 'الدعوات',
    invitationsEmpty: 'لا توجد دعوات مفتوحة.',
    invite: 'دعوة مساهم',
    inviteEmail: 'البريد الإلكتروني',
    inviteRole: 'الدور',
    inviteSubmit: 'إنشاء الدعوة',
    inviteCreatedTitle: 'أُنشئت الدعوة',
    inviteCreatedBody:
      'شارك هذا الرابط أحادي الاستخدام مع من دعوته. تنتهي صلاحيته خلال سبعة أيام.',
    copyLink: 'نسخ الرابط',
    linkCopied: 'نُسخ الرابط.',
    withdraw: 'سحب',
    changeRole: 'تغيير الدور',
    suspend: 'إيقاف',
    restore: 'استعادة',
    deauthorize: 'إنهاء التصريح',
    deauthorizeTitle: 'إنهاء تصريح هذا الحساب',
    deauthorizeBody: (name) =>
      `يفقد ${name} الوصول نهائيًا. يبقى سجل مهامه ومساهماته ووصوله محفوظًا وباسمه.`,
    deauthorizeEffects: [
      'تنتهي جلساته فورًا.',
      'لا يمكنه تسجيل الدخول مجددًا، ولا توجد استعادة.',
      'العودة تتطلب دعوة جديدة تُستخدم عند تسجيل الدخول.',
      'يبقى سجله محفوظًا دون تغيير.',
    ],
    accessHistory: 'سجل الوصول',
    accessHistoryEmpty: 'لا توجد أحداث وصول مسجلة بعد.',
    accessHistoryColumns: {
      when: 'الوقت',
      actor: 'من تصرّف',
      action: 'الإجراء',
      subject: 'على من',
    },
    statuses: { active: 'نشط', disabled: 'موقوف', deauthorized: 'منتهي التصريح' },
    lastOwnerTitle: 'آخر مالك نشط محمي',
    lastOwnerBody:
      'يرفض النظام تنزيل رتبة المالك النشط الوحيد أو إيقافه أو إزالته — وإلا لن يستطيع أحد إدارة الوصول. أضف مالكًا آخر أولًا.',
    suspendTitle: 'إيقاف هذا الحساب',
    suspendBody: (name) =>
      `سيفقد ${name} الوصول عند طلبه التالي. يبقى سجل مساهماته دون تغيير.`,
    suspendEffects: [
      'تنتهي جلساته فورًا.',
      'لا يمكنه تسجيل الدخول حتى الاستعادة.',
      'يبقى سجل مساهماته محفوظًا.',
    ],
    roleTitle: (name) => `تغيير دور ${name}`,
    roleBody: 'اختر الدور الذي سيحمله هذا الشخص من الآن فصاعدًا.',
    roleEffects: [
      'تنتهي جلساته ويسجّل الدخول مجددًا بالدور الجديد.',
      'يتغيّر ما يراه ويفعله فورًا.',
    ],
    provider: 'المزوّد',
    providerEmail: 'بريد المزوّد',
    invitedAs: 'دُعي بصفة',
    emailUnverified: 'غير موثَّق',
  },
};
